import React, {createContext, useContext, useMemo} from 'react'
import {GameAction, PopupInformation, UserReportReasonEnum} from '@colonist/game-base-types'
import {CardEnum, CityImprovementTypes, PlayerColor} from '@colonist/game-states'
// eslint-disable-next-line no-restricted-imports
import {UIGameMechanic} from '../../../../code/client/shared/scripts/ui_game/UIGameEventController'
import {CardPopupInformation} from '@colonist/game-card-types'

interface UIGameSocketContextProps {
    sendReportPlayer: (reportReason: UserReportReasonEnum, color: PlayerColor) => void
    sendLeaveGame: () => void
    sendResignGame: () => void
    sendDisableKarma: () => void
    registerUIGameMechanic: (uiGameMechanic: UIGameMechanic) => void
    sendForceOpenGuideBubble: () => void
    sendClickedPassTurn: () => void
    sendRequestSpecialBuildPhase: () => void
    sendCancelSpecialBuildPhase: () => void
    sendCancelAction: () => void
    sendMoveShip: () => void
    sendClearRoadEdgeHighlight: () => void
    sendBuildShip: () => void
    sendClearCornerHighlight: () => void
    sendClearTileHighlight: () => void
    sendBuildSettlement: () => void
    sendBuildRoad: () => void
    sendBuildCityWall: () => void
    sendBuildCity: () => void
    sendBuyDevCard: () => void
    sendUpgradeKnight: () => void
    sendPlaceKnight: () => void
    sendTakeKnightAction: () => void
    sendActivateKnight: () => void
    sendConfirmCityUpgrade: (cityImprovementType: CityImprovementTypes) => void
    sendClickedDevelopmentCard: (cardEnum: CardEnum) => void
    sendRequestActionSwap: (gameAction: GameAction) => void
    sendClickedDice: () => void
    sendConfirmUseDevelopmentCard: (popupInformation: CardPopupInformation | PopupInformation) => void
    sendDiceNumberAnimationEnd: (diceValue: number) => void
}

const UIGameSocketContext = createContext<UIGameSocketContextProps | undefined>(undefined)

export const useUIGameSocketContext = () => {
    const context = useContext(UIGameSocketContext)
    if(context === undefined) {
        throw new Error('useUIGameSocketContext must be used within a UIGameSocketProvider')
    }
    return context
}

interface UIGameSocketProviderProps {
    sendReportPlayer: (reportReason: UserReportReasonEnum, color: PlayerColor) => void
    sendLeaveGame: () => void
    sendResignGame: () => void
    sendDisableKarma: () => void
    registerUIGameMechanic: (uiGameMechanic: UIGameMechanic) => void
    sendForceOpenGuideBubble: () => void
    sendClickedPassTurn: () => void
    sendRequestSpecialBuildPhase: () => void
    sendCancelSpecialBuildPhase: () => void
    sendCancelAction: () => void
    sendMoveShip: () => void
    sendClearRoadEdgeHighlight: () => void
    sendBuildShip: () => void
    sendClearCornerHighlight: () => void
    sendClearTileHighlight: () => void
    sendBuildSettlement: () => void
    sendBuildRoad: () => void
    sendBuildCityWall: () => void
    sendBuildCity: () => void
    sendBuyDevCard: () => void
    sendUpgradeKnight: () => void
    sendPlaceKnight: () => void
    sendTakeKnightAction: () => void
    sendActivateKnight: () => void
    sendConfirmCityUpgrade: (cityImprovementType: CityImprovementTypes) => void
    sendClickedDevelopmentCard: (cardEnum: CardEnum) => void
    sendRequestActionSwap: (gameAction: GameAction) => void
    sendClickedDice: () => void
    sendConfirmUseDevelopmentCard: (popupInformation: CardPopupInformation | PopupInformation) => void
    sendDiceNumberAnimationEnd: (diceValue: number) => void
    children: React.ReactNode
}

export const UIGameSocketProvider = ({
    sendReportPlayer,
    sendLeaveGame,
    sendResignGame,
    sendDisableKarma,
    registerUIGameMechanic,
    sendForceOpenGuideBubble,
    sendClickedPassTurn,
    sendRequestSpecialBuildPhase,
    sendCancelSpecialBuildPhase,
    sendCancelAction,
    sendMoveShip,
    sendClearRoadEdgeHighlight,
    sendBuildShip,
    sendClearCornerHighlight,
    sendClearTileHighlight,
    sendBuildSettlement,
    sendBuildRoad,
    sendBuildCityWall,
    sendBuildCity,
    sendBuyDevCard,
    sendUpgradeKnight,
    sendPlaceKnight,
    sendTakeKnightAction,
    sendActivateKnight,
    sendConfirmCityUpgrade,
    sendClickedDevelopmentCard,
    sendRequestActionSwap,
    sendClickedDice,
    sendConfirmUseDevelopmentCard,
    sendDiceNumberAnimationEnd,
    children,
}: UIGameSocketProviderProps) => {
    const memoizedContextValue = useMemo(() => ({
        sendReportPlayer: sendReportPlayer,
        sendLeaveGame: sendLeaveGame,
        sendResignGame: sendResignGame,
        sendDisableKarma: sendDisableKarma,
        registerUIGameMechanic: registerUIGameMechanic,
        sendForceOpenGuideBubble: sendForceOpenGuideBubble,
        sendClickedPassTurn: sendClickedPassTurn,
        sendRequestSpecialBuildPhase: sendRequestSpecialBuildPhase,
        sendCancelSpecialBuildPhase: sendCancelSpecialBuildPhase,
        sendCancelAction: sendCancelAction,
        sendMoveShip: sendMoveShip,
        sendClearRoadEdgeHighlight: sendClearRoadEdgeHighlight,
        sendBuildShip: sendBuildShip,
        sendClearCornerHighlight: sendClearCornerHighlight,
        sendClearTileHighlight: sendClearTileHighlight,
        sendBuildSettlement: sendBuildSettlement,
        sendBuildRoad: sendBuildRoad,
        sendBuildCityWall: sendBuildCityWall,
        sendBuildCity: sendBuildCity,
        sendBuyDevCard: sendBuyDevCard,
        sendUpgradeKnight: sendUpgradeKnight,
        sendPlaceKnight: sendPlaceKnight,
        sendTakeKnightAction: sendTakeKnightAction,
        sendActivateKnight: sendActivateKnight,
        sendConfirmCityUpgrade: sendConfirmCityUpgrade,
        sendClickedDevelopmentCard: sendClickedDevelopmentCard,
        sendRequestActionSwap: sendRequestActionSwap,
        sendClickedDice: sendClickedDice,
        sendConfirmUseDevelopmentCard: sendConfirmUseDevelopmentCard,
        sendDiceNumberAnimationEnd: sendDiceNumberAnimationEnd,
    }), [
        sendReportPlayer,
        sendLeaveGame,
        sendResignGame,
        sendDisableKarma,
        registerUIGameMechanic,
        sendForceOpenGuideBubble,
        sendClickedPassTurn,
        sendRequestSpecialBuildPhase,
        sendCancelSpecialBuildPhase,
        sendCancelAction,
        sendMoveShip,
        sendClearRoadEdgeHighlight,
        sendBuildShip,
        sendClearCornerHighlight,
        sendClearTileHighlight,
        sendBuildSettlement,
        sendBuildRoad,
        sendBuildCityWall,
        sendBuildCity,
        sendBuyDevCard,
        sendUpgradeKnight,
        sendPlaceKnight,
        sendTakeKnightAction,
        sendActivateKnight,
        sendConfirmCityUpgrade,
        sendClickedDevelopmentCard,
        sendRequestActionSwap,
        sendClickedDice,
        sendConfirmUseDevelopmentCard,
        sendDiceNumberAnimationEnd,
    ])
    return (
        <UIGameSocketContext.Provider value={memoizedContextValue}>
            {children}
        </UIGameSocketContext.Provider>
    )
}
